import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowsClockwise } from "@phosphor-icons/react";
import PhotoAttacher, { AttachedMedia } from "@/components/vansha/PhotoAttacher";
import { BookIcon, SunBloom } from "@/components/vansha/Graphics";

export default function Journal() {
  const [items, setItems] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [body, setBody] = useState("");
  const [mood, setMood] = useState("");
  const [memoryIds, setMemoryIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPrompt = async () => { const r = await api.get("/journal/prompt"); setPrompt(r.data.prompt); };
  const load = async () => { const r = await api.get("/journal"); setItems(r.data); };
  useEffect(() => { loadPrompt(); load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/journal", { prompt, body, mood, memory_ids: memoryIds });
      toast.success("Entry saved.");
      setBody(""); setMood(""); setMemoryIds([]);
      loadPrompt();
      load();
    } catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="journal-page">
      <header className="relative overflow-hidden vansha-card-tinted-blush p-8 sm:p-10">
        <div className="absolute -right-4 -top-4 opacity-90 hidden md:block pointer-events-none">
          <SunBloom size={180} />
        </div>
        <div className="absolute right-24 top-6 opacity-95 hidden md:block pointer-events-none">
          <BookIcon size={90} />
        </div>
        <div className="relative z-10 max-w-md">
          <p className="overline text-terracotta mb-2">Daily journal</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-none">
            A page <em className="text-terracotta font-normal italic">a day</em>
          </h1>
          <p className="text-ink-700 mt-3 leading-relaxed">Small reflections that, in years to come, will mean everything.</p>
        </div>
      </header>

      <form onSubmit={submit} className="vansha-card p-8 space-y-6" data-testid="journal-form">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="overline mb-2">Today's prompt</p>
            <p className="font-serif text-2xl text-ink-900 italic">"{prompt}"</p>
          </div>
          <button type="button" onClick={loadPrompt} className="btn-ghost p-2 flex-shrink-0" title="New prompt" data-testid="journal-new-prompt"><ArrowsClockwise weight="thin" size={18} /></button>
        </div>
        <textarea required value={body} onChange={(e) => setBody(e.target.value)} className="input-line font-serif text-lg" rows="6" placeholder="Write what you wish to remember…" data-testid="journal-body" />
        <div className="flex flex-wrap items-center gap-3">
          <span className="overline">Mood</span>
          {["Grateful", "Anxious", "Tired", "Hopeful", "Tender"].map((m) => (
            <button type="button" key={m} onClick={() => setMood(m === mood ? "" : m)} className={`text-sm px-3 py-1 rounded-full border ${mood === m ? "bg-terracotta text-white border-terracotta" : "border-ink-20 text-ink-700"}`} data-testid={`mood-${m}`}>{m}</button>
          ))}
        </div>
        <PhotoAttacher value={memoryIds} onChange={setMemoryIds} label="Attach photos" />
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="journal-submit"><Plus weight="thin" size={18} /> {busy ? "Saving…" : "Add entry"}</button>
        </div>
      </form>

      {items.length === 0 ? (
        <p className="text-ink-500" data-testid="journal-empty">No entries yet. Today is a good day to begin.</p>
      ) : (
        <div className="space-y-4">
          {items.map((j) => (
            <div key={j.id} className="vansha-card p-6" data-testid={`journal-${j.id}`}>
              <div className="flex items-baseline justify-between mb-2 gap-3">
                <p className="overline text-saffron">{new Date(j.created_at).toLocaleDateString()}</p>
                {j.mood && <span className="text-xs px-2 py-0.5 rounded-full bg-vansha-accent text-ink-700">{j.mood}</span>}
              </div>
              {j.prompt && <p className="italic text-ink-500 text-sm mb-2">"{j.prompt}"</p>}
              <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap">{j.body}</p>
              <AttachedMedia ids={j.memory_ids} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
